import * as React from 'react';
import { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert, Paper, Collapse, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import axiosInstance from '../../authentication/AuthContext';
import { useAuth } from '../../authentication/AuthContext';
import { StyledDataGrid } from '../customizations/dataGrid';


// JSON Display Component
const JsonDisplay = ({ data }) => {
    const [expanded, setExpanded] = useState(false);
    const jsonString = JSON.stringify(data, null, 2);
    const isLarge = jsonString.length > 100;

    // Create excerpt - first 100 characters, cleaned up
    const createExcerpt = (str) => {
        if (str.length <= 100) return str;
        const excerpt = str.substring(0, 100).trim();
        // Try to end at a word boundary
        const lastSpace = excerpt.lastIndexOf(' ');
        return lastSpace > 50 ? excerpt.substring(0, lastSpace) + '...' : excerpt + '...';
    };

    if (!data || Object.keys(data).length === 0) {
        return <Typography variant="body2" color="text.secondary">No data</Typography>;
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 'bold',
                        color: 'primary.main',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        wordBreak: 'break-word'
                    }}
                >
                    {createExcerpt(jsonString)}
                </Typography>
                {isLarge && (
                    <IconButton
                        size="small"
                        onClick={() => setExpanded(!expanded)}
                        sx={{ p: 0.5 }}
                    >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                )}
            </Box>

            {isLarge ? (
                <Collapse in={expanded}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 1.5,
                            mt: 1,
                            backgroundColor: 'grey.50',
                            border: 1,
                            borderColor: 'grey.200',
                            borderRadius: 1,
                            maxHeight: '200px',
                            overflow: 'auto',
                        }}
                    >
                        <Typography
                            component="pre"
                            variant="caption"
                            sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                lineHeight: 1.4,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: 'text.primary',
                                margin: 0,
                            }}
                        >
                            {jsonString}
                        </Typography>
                    </Paper>
                </Collapse>
            ) : (
                <Paper
                    elevation={0}
                    sx={{
                        p: 1,
                        mt: 1,
                        backgroundColor: 'grey.50',
                        border: 1,
                        borderColor: 'grey.200',
                        borderRadius: 1,
                    }}
                >
                    <Typography
                        component="pre"
                        variant="caption"
                        sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            lineHeight: 1.4,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            color: 'text.primary',
                            margin: 0,
                        }}
                    >
                        {jsonString}
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

export default function AnalyticsGrid() {
    const { accessToken } = useAuth();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Analytics columns
    const columns = [
        { field: 'event_type', headerName: 'Event Type', flex: 1, minWidth: 180 },
        { field: 'run_id', headerName: 'Run ID', width: 120,
            renderCell: (params) => {
                return `...${params.value.slice(0, 8)}`;
            }
        },
        { field: 'latency', headerName: 'Latency (s)', width: 120,
            renderCell: (params) => {
                return params.value ? params.value.toFixed(2) : '-';
            }
        },
        { field: 'token_usage', headerName: 'Token Usage', width: 120,
            renderCell: (params) => {
                const event = params.row;
                if (event.event_type === 'llm_end' && event.data?.token_usage?.total_tokens) {
                    return event.data.token_usage.total_tokens;
                }
                return '-';
            }
        },
        { field: 'data', headerName: 'Data', flex: 1, minWidth: 200,
            renderCell: (params) => {
                return <JsonDisplay data={params.value} />;
            }
        },
        { field: 'timestamp', headerName: 'Timestamp', width: 150,
            renderCell: (params) => {
                return new Date(params.value).toLocaleString();
            }
        }
    ];

    // Fetch analytics
    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                setLoading(true);
                const response = await axiosInstance.get('/analytics/', {
                    headers: { 'Authorization': `Bearer ${accessToken}` },
                    withCredentials: true,
                });
                setEvents(response.data);
            } catch (err) {
                console.error('Failed to fetch analytics:', err);
                setError('Failed to load analytics: ' + err);
            } finally {
                setLoading(false);
            }
        };
        if (accessToken) {
            fetchAnalytics();
        }
    }, [accessToken]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Paper variant="outlined" sx={{ elevation: 1, mb: 2 }}>
            <Box sx={{ p: 2 , mb: 2 }}>
            <Typography variant="h6" gutterBottom>
                Analytics
            </Typography>
            <Typography variant="caption" gutterBottom>
                {events.length} Metrics found
            </Typography>
            {events.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ p: 2 }}>
                    No analytics data found
                </Typography>
            ) : (
                <StyledDataGrid
                    rows={events}
                    columns={columns}
                    getRowId={(row) => row.id}
                    loading={loading}
                    disableRowSelectionOnClick
                    initialState={{
                        pagination: { paginationModel: { pageSize: 20 } },
                        filter: {
                            filterModel: {
                                items: [
                                    {
                                        field: 'event_type',
                                        operator: 'equals',
                                        value: 'llm_end',
                                    },
                                    {
                                        field: 'event_type',
                                        operator: 'equals',
                                        value: 'llm_start',
                                    },
                                ],
                                logicOperator: 'or',
                            },
                        },
                    }}
                    pageSizeOptions={[10, 20, 50]}
                    disableColumnResize
                    density="compact"
                    sx={{
                        '& .MuiDataGrid-row': {
                            backgroundColor: 'white',
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: '#f5f5f5',
                        },
                        '& .MuiDataGrid-row.Mui-selected': {
                            backgroundColor: '#e3f2fd',
                        },
                        '& .MuiDataGrid-row.Mui-selected:hover': {
                            backgroundColor: '#bbdefb',
                        },
                    }}
                    slotProps={{
                        filterPanel: {
                            filterFormProps: {
                                logicOperatorInputProps: {
                                    variant: 'outlined',
                                    size: 'small',
                                },
                                columnInputProps: {
                                    variant: 'outlined',
                                    size: 'small',
                                    sx: { mt: 'auto' },
                                },
                                operatorInputProps: {
                                    variant: 'outlined',
                                    size: 'small',
                                    sx: { mt: 'auto' },
                                },
                                valueInputProps: {
                                    InputComponentProps: {
                                        variant: 'outlined',
                                        size: 'small',
                                    },
                                },
                            },
                        },
                    }}
                />
            )}
            </Box>
        </Paper>
    );
}